import React from 'react';
import { Warehouse, Truck, Package, ClipboardCheck, BarChart3, Route, Boxes, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { getBaseUrl } from '../components/JsonLd';

const Services = () => {
  const services = [
    {
      icon: Warehouse,
      title: 'Sandėliavimas',
      anchorId: 'sandeliavimas',
      description: 'Trumpalaikis ir ilgalaikis prekių saugojimas, pritaikytas įvairiems prekių srautams.',
      benefits: [
        'Nereikia investuoti į nuosavą sandėlį',
        'Lankstumas sezoniškumo metu',
        'Profesionali apsauga ir priežiūra'
      ],
      result: 'Saugios ir tvarkingos atsargos',
      image: '/images/photo-1645736315000-6f788915923b.jpeg'
    },
    {
      icon: Truck,
      title: 'Krovos darbai',
      anchorId: 'krovos-darbai',
      description: 'Mechanizuotas ir rankinis pakrovimas bei iškrovimas su profesionalia įranga.',
      benefits: [
        'Greitas aptarnavimas',
        'Saugus prekių tvarkymas',
        'Patyrę operatoriai'
      ],
      result: 'Greitas ir saugus prekių judėjimas',
      image: '/images/photo-1714627798569-b3e36d409c4b.jpeg'
    },
    {
      icon: Package,
      title: 'Prekių paruošimas',
      anchorId: 'prekiu-paruosimas',
      description: 'Komplektavimas, perpakavimas, ženklinimas, svėrimas ir rūšiavimas pagal jūsų reikalavimus.',
      benefits: [
        'Tikslus komplektavimas',
        'Kokybės kontrolė',
        'Lankstūs sprendimai'
      ],
      result: 'Paruoštos prekės pagal jūsų reikalavimus',
      image: '/images/photo-1761195696590-3490ea770aa1.jpeg'
    },
    {
      icon: ClipboardCheck,
      title: 'Užsakymų valdymas',
      anchorId: 'uzsakymu-valdymas',
      description: 'Tikslus prekių atrinkimas taikant FIFO (First In, First Out) ir FEFO (First Expired, First Out) metodus.',
      benefits: [
        'FIFO/FEFO metodai',
        'Sumažinti nuostoliai',
        'Tikslūs terminai'
      ],
      result: 'Mažesni nuostoliai ir tiksli logistika',
      image: '/images/photo-1604233707683-fac593c74600.jpeg'
    },
    {
      icon: BarChart3,
      title: 'Apskaita ir kontrolė',
      anchorId: 'apskaita-ir-kontrole',
      description: 'Nuolatinės ataskaitos apie likučius ir prekių judėjimą realiu laiku.',
      benefits: [
        'Realaus laiko ataskaitos',
        'WMS sistema',
        'Pilna skaidrumas'
      ],
      result: 'Pilna kontrolė ir skaidrumas',
      image: '/images/photo-1764745021344-317b80f09e40.jpeg'
    },
    {
      icon: Route,
      title: 'Logistikos organizavimas',
      anchorId: 'logistikos-organizavimas',
      description: 'Pakavimas ir transportavimo sprendimų koordinavimas su jūsų partneriais.',
      benefits: [
        'Koordinuotas transportas',
        'Optimizuoti maršrutai',
        'Laiko sutaupymas'
      ],
      result: 'Efektyvus krovinių judėjimas',
      image: '/images/photo-1758542113402-b46079642dc9.jpeg'
    },
    {
      icon: Boxes,
      title: '3PL logistika',
      anchorId: '3pl',
      description: 'Pilnas sandėliavimo, prekių tvarkymo ir distribucijos procesų valdymas.',
      benefits: [
        'Visapusiškas sprendimas',
        'Mažesnės sąnaudos',
        'Profesionalus valdymas'
      ],
      result: 'Koncentruokitės į savo verslą, mes tvarkome logistiką',
      image: '/images/pexels-photo-35501718.jpeg'
    },
  ];

  const baseUrl = getBaseUrl();
  const servicesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${baseUrl}paslaugos#services`,
    name: 'WSK logistikos paslaugos',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#sandeliavimas`,
          name: 'Sandėliavimas',
          serviceType: 'Sandėliavimo paslaugos',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Trumpalaikis ir ilgalaikis prekių saugojimas moderniame sandėlyje Kaune.'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#krovos-darbai`,
          name: 'Krovos darbai',
          serviceType: 'Krovos darbai',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Mechanizuotas ir rankinis krovinių pakrovimas bei iškrovimas.'
        }
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#prekiu-paruosimas`,
          name: 'Prekių paruošimas',
          serviceType: 'Komplektavimas, perpakavimas, ženklinimas',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Komplektavimas, perpakavimas, ženklinimas, svėrimas, rūšiavimas pagal kliento poreikius.'
        }
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#uzsakymu-valdymas`,
          name: 'Užsakymų valdymas (FIFO / FEFO)',
          serviceType: 'Užsakymų valdymas ir atrinkimas',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Detalus arba paletinis prekių atrinkimas taikant FIFO ir FEFO metodus.'
        }
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#apskaita-ir-kontrole`,
          name: 'Apskaita ir kontrolė',
          serviceType: 'Atsargų apskaita ir ataskaitos',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Tikslios ataskaitos apie likučius, judėjimą ir prekių apyvartumą.'
        }
      },
      {
        '@type': 'ListItem',
        position: 6,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#logistikos-organizavimas`,
          name: 'Logistikos organizavimas',
          serviceType: 'Pakavimas ir transportavimo organizavimas',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Siuntų pakavimo ir transportavimo organizavimo paslaugos pagal kliento poreikį.'
        }
      },
      {
        '@type': 'ListItem',
        position: 7,
        item: {
          '@type': 'Service',
          '@id': `${baseUrl}paslaugos#3pl`,
          name: '3PL (trečiųjų šalių logistika)',
          serviceType: '3PL logistika',
          provider: { '@id': `${baseUrl}#localbusiness` },
          areaServed: 'Lithuania',
          description: 'Kompleksinis sandėliavimo, prekių tvarkymo ir distribucijos valdymas.'
        }
      }
    ]
  };

  return (
    <div className="min-h-screen" data-testid="services-page">
      <SEO 
        title="Logistikos paslaugos verslui | Sandėliavimas, 3PL, krovos darbai"
        description="Sandėliavimas, prekių komplektavimas, FIFO/FEFO užsakymų valdymas ir 3PL logistika. Lankstūs sprendimai pagal Jūsų verslo poreikius."
        keywords="sandėliavimas, 3PL logistika, krovos darbai, prekių komplektavimas, užsakymų valdymas, FIFO, FEFO, logistikos organizavimas"
      />
      <JsonLd id="services-list" data={servicesJsonLd} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Mūsų paslaugos</h1>
            <p className="text-xl text-blue-100">
              Visapusiški logistikos sprendimai, pritaikyti jūsų verslo poreikiams ir tikslams
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  id={service.anchorId}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'lg:grid-flow-dense'
                  }`}
                  data-testid={`service-detail-${index}`}
                >
                  {/* Image */}
                  <div className={`${isEven ? '' : 'lg:col-start-2'} relative h-80 rounded-xl overflow-hidden shadow-xl`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="bg-blue-600 p-3 rounded-lg shadow-lg">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h2>
                    <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Naudinga, nes:</h3>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                      <p className="text-blue-900 font-medium">
                        <strong>Rezultatas:</strong> {service.result}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Norite sužinoti daugiau apie mūsų paslaugas?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Susisiekite su mumis ir gaukite individualų pasiūlymą
          </p>
          <a
            href="/kontaktai"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-xl"
          >
            Susisiekti
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;
